/* The Hero's Circle. A button-driven character browser with a "power up"
 * mechanic. No swipe, no drag.
 *
 * Flow per card:
 *   1. User presses Like. A tier-colored heart flies from the button into
 *      the character's image, the card enters powered-up state with a
 *      tier-colored ring, and a 2.8s hold timer starts.
 *   2. During the hold window, tapping the IMAGE (or pressing Like again)
 *      adds a bonus heart. Cap of 3 hearts per character per session.
 *      1st heart gold, 2nd blue, 3rd purple.
 *   3. After 3 hearts OR the timer expiring, the card slides off and the
 *      next character is dealt. Pressing Skip at any time advances too.
 *
 * Trial pool: flat 10 hearts per day, no unlock bonus inside the Trial.
 * Completion bonus: at the summary screen the host grants +5 main-hall
 * hearts via window.hohGrantTrialCompBonus(), once per day.
 *
 * Coupling to the host page:
 *   window.hohGetAllCharacters()    pool source
 *   window.hohSupabaseClient        increment_hearts RPC
 *   window.hohGetRarity()           rarity lookup for tier classes
 *   window.hohGrantTrialCompBonus() one-time-per-day +5 hall hearts
 *   writes to 'hoh-hearts'          shares with the gallery's My hearts
 *                                   sort and lifetime counter
 */
(function(){
  /* 10 hearts to give per daily session, one heart per character.
     Hero's Circle is now an OPT-IN discovery game inside Fresh
     Faces — no longer the daily quest. Reduced from 20 → 10 hearts
     so it reads as a quick activity. Completing the round grants
     +10 hearts back to the user's bank as a reward (no raffle
     tickets — those come exclusively from the Explore the Hall
     daily quest now). */
  var TRIAL_DAILY_HEARTS = 10;
  var TRIAL_COMPLETION_BONUS_HEARTS = 10;
  /* Per-character cap inside a single Trial session. Single heart so
     the activity stays brisk and predictable. */
  var TRIAL_PER_CHAR_CAP = 1;
  /* Power-up hold timing. Initial hold after the first heart is short so
     the flow feels brisk; each bonus heart awarded extends the window by
     a small amount so a tapping user is not rushed but a passive user is
     not held hostage. Reaching the 3-heart cap fires a quick exit. */
  var POWERUP_HOLD_INITIAL_MS = 1400;
  var POWERUP_HOLD_EXTEND_MS  = 700;
  var POWERUP_HOLD_MAX_MS     = 500;

  /* ============ helpers ============ */
  function todayKey(){ var d = new Date(); return d.getFullYear() + '-' + (d.getMonth()+1) + '-' + d.getDate(); }
  function sget(k,d){ try { var v = localStorage.getItem(k); return v == null ? d : v; } catch(e){ return d; } }
  function sset(k,v){ try { localStorage.setItem(k,v); } catch(e){} }
  function sgetJ(k,d){ try { var v = localStorage.getItem(k); if(!v) return d; var p = JSON.parse(v); return p == null ? d : p; } catch(e){ return d; } }
  function ssetJ(k,v){ try { localStorage.setItem(k, JSON.stringify(v)); } catch(e){} }
  function esc(s){ if (s == null) return ''; return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;'); }
  function shuffle(a){ a = a.slice(); for (var i = a.length - 1; i > 0; i--){ var j = Math.floor(Math.random()*(i+1)); var t = a[i]; a[i] = a[j]; a[j] = t; } return a; }

  /* Mirror the liked id into the main gallery's lifetime heart map so the
     "My hearts" sort surfaces Trial likes and so the per-character chip
     (xN) and rarity tier reflect hearts given through the Trial. The main
     gallery stores 'hoh-hearts' as an id->count object; if an older
     array form is present it gets migrated to the object shape here. */
  function mirrorHearted(id){
    try {
      var raw = localStorage.getItem('hoh-hearts');
      var v = raw ? JSON.parse(raw) : {};
      if (Array.isArray(v)){ var mig = {}; v.forEach(function(i){ mig[i] = 1; }); v = mig; }
      if (!v || typeof v !== 'object') v = {};
      v[id] = (v[id] || 0) + 1;
      localStorage.setItem('hoh-hearts', JSON.stringify(v));
    } catch(e){}
  }

  /* ============ daily state keys ============ */
  function heartsKey(){ return 'hoh-trial-hearts-' + todayKey(); }
  function seenKey(){ return 'hoh-trial-seen-' + todayKey(); }
  function likedKey(){ return 'hoh-trial-liked-' + todayKey(); }
  function deckKey(){ return 'hoh-trial-deck-' + todayKey(); }
  function introKey(){ return 'hoh-trial-intro-' + todayKey(); }

  function getHearts(){
    var raw = sget(heartsKey(), null);
    if (raw == null){ sset(heartsKey(), String(TRIAL_DAILY_HEARTS)); return TRIAL_DAILY_HEARTS; }
    return parseInt(raw, 10) || 0;
  }
  function setHearts(n){ sset(heartsKey(), String(Math.max(0, n))); }
  function getSeen(){ return sgetJ(seenKey(), []); }
  function addSeen(id){ var s = getSeen(); if (s.indexOf(id) === -1){ s.push(id); ssetJ(seenKey(), s); } }
  function getLiked(){ return sgetJ(likedKey(), {}); }
  function addLiked(c){
    /* Up to 3 hearts per character per Trial session. The summary lists
       characters with 2+ as bonus recipients. */
    var liked = getLiked();
    var entry = liked[c.id] || { id: c.id, name: c.name, image_url: c.image_thumb_url || c.image_url || '', hearts: 0 };
    entry.hearts = Math.min(TRIAL_PER_CHAR_CAP, (entry.hearts || 0) + 1);
    liked[c.id] = entry;
    ssetJ(likedKey(), liked);
    return entry;
  }
  function getCharHearts(id){
    var l = getLiked();
    return (l[id] && l[id].hearts) || 0;
  }
  function introSeenToday(){ return sget(introKey(), null) === '1'; }
  function markIntroSeen(){ sset(introKey(), '1'); }

  /* ============ deck ============ */
  function buildOrLoadDeck(){
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if (!all || !all.length) return [];
    var seen = getSeen();
    var stored = sgetJ(deckKey(), null);
    if (stored && stored.length){
      var present = {};
      all.forEach(function(c){ present[c.id] = c; });
      var rebuilt = [];
      stored.forEach(function(id){ if (present[id] && seen.indexOf(id) === -1) rebuilt.push(id); });
      if (rebuilt.length) return rebuilt;
    }
    var eligible = all.filter(function(c){ return c && c.id && seen.indexOf(c.id) === -1; });
    var ids = shuffle(eligible.map(function(c){ return c.id; }));
    ssetJ(deckKey(), ids);
    return ids;
  }
  function saveDeck(ids){ ssetJ(deckKey(), ids); }

  function resolveChar(id){
    var all = window.hohGetAllCharacters ? window.hohGetAllCharacters() : [];
    for (var i = 0; i < all.length; i++) if (all[i].id === id) return all[i];
    return null;
  }

  /* ============ runtime state ============ */
  var state = {
    deck: [],
    totalToday: 0,
    cardExpanded: false,        /* story expanded over media */
    cardImageExpanded: false,   /* media zoom (kept off in powered-up state) */
    cardImgIdx: 0,
    /* Power-up state: when a card has received its first heart and is
       holding open for bonus taps. */
    poweredUpId: null,
    poweredUpHearts: 0,
    poweredUpTimer: null,
    /* Lock to prevent double-fire on rapid taps during the off animation. */
    advancing: false
  };

  /* ============ screen management ============ */
  function showScreen(which){
    ['intro','swiper','summary'].forEach(function(name){
      var el = document.getElementById('hoh-trial-' + name);
      if (el) el.style.display = (name === which) ? '' : 'none';
    });
    var closeBtn = document.getElementById('hoh-trial-close-btn');
    if (closeBtn){ closeBtn.style.display = (which === 'intro') ? 'none' : 'flex'; }
  }

  /* ============ hearts row ============ */
  function renderHearts(animateRefill){
    var el = document.getElementById('hoh-trial-hearts');
    if (!el) return;
    var remaining = getHearts();
    var cap = TRIAL_DAILY_HEARTS;
    var html = '';
    for (var i = 0; i < cap; i++){
      var filled = i < remaining;
      var glyph = filled ? '&#9829;' : '&#9825;';
      html += '<span class="hoh-trial-heart ' + (filled ? 'hoh-trial-heart-filled' : 'hoh-trial-heart-empty') + '" data-idx="' + i + '">' + glyph + '</span>';
    }
    el.innerHTML = html;
    if (animateRefill){
      var hearts = el.querySelectorAll('.hoh-trial-heart-filled');
      hearts.forEach(function(h, idx){
        h.style.animationDelay = (idx * 55) + 'ms';
        h.classList.add('hoh-trial-heart-refill');
      });
    }
  }
  function animateHeartSpend(){
    var el = document.getElementById('hoh-trial-hearts');
    if (!el) return;
    var hearts = el.querySelectorAll('.hoh-trial-heart-filled');
    if (!hearts.length) return;
    var last = hearts[hearts.length - 1];
    last.classList.add('hoh-trial-heart-spend');
    setTimeout(function(){ last.innerHTML = '&#9825;'; }, 150);
    setTimeout(function(){ renderHearts(false); }, 320);
  }

  function getImgsForCard(c){
    var a = [];
    if (c.image_url) a.push(c.image_url);
    if (c.image_url_2) a.push(c.image_url_2);
    return a;
  }

  /* ============ card render ============ */
  function topCardHtml(c){
    if (!c) return '';
    var imgs = getImgsForCard(c);
    var hasMulti = imgs.length > 1;
    var curIdx = Math.min(state.cardImgIdx, Math.max(0, imgs.length - 1));
    var imgUrl = imgs[curIdx] || '';
    var firstLetter = esc((c.name || '?').charAt(0));
    var lv = c.level ? ' &middot; Level ' + esc(c.level) : '';
    var campaign = c.campaign ? '<p class="hoh-trial-card-campaign">' + esc(c.campaign) + '</p>' : '';
    var fullStory = c.story ? esc(String(c.story)) : '';
    /* Rarity tier from the host. Subtle outline / halo via CSS. */
    var rar = (typeof window.hohGetRarity === 'function')
      ? window.hohGetRarity(c.hearts)
      : { tier: 'common' };
    var dotsHtml = '';
    if (hasMulti){
      dotsHtml = '<div class="hoh-trial-card-dots" onclick="event.stopPropagation();hohTrialCycleImage()">';
      for (var i = 0; i < imgs.length; i++){
        dotsHtml += '<span class="hoh-trial-card-dot' + (i === curIdx ? ' hoh-trial-card-dot-active' : '') + '"></span>';
      }
      dotsHtml += '</div>';
    }
    var arrowsHtml = '';
    if (hasMulti){
      arrowsHtml =
        '<button type="button" class="hoh-trial-card-arrow hoh-trial-card-arrow-prev" onclick="event.stopPropagation();hohTrialCycleImage(-1)" aria-label="Previous photo">&#8249;</button>' +
        '<button type="button" class="hoh-trial-card-arrow hoh-trial-card-arrow-next" onclick="event.stopPropagation();hohTrialCycleImage(1)" aria-label="Next photo">&#8250;</button>';
    }
    var media = imgUrl
      ? '<img class="hoh-trial-card-img" src="' + esc(imgUrl) + '" alt="' + esc(c.name || '') + '" crossorigin="anonymous" data-letter="' + firstLetter + '" onerror="if(window.hohImgFallback)hohImgFallback(this,\'hoh-trial-card-letter\');else this.style.display=\'none\';">'
      : '<div class="hoh-trial-card-letter">' + firstLetter + '</div>';
    var cardClasses = 'hoh-trial-card hoh-trial-card-top hoh-trial-rar-' + rar.tier;
    if (state.cardExpanded) cardClasses += ' hoh-trial-card-expanded';
    if (state.cardImageExpanded) cardClasses += ' hoh-trial-card-image-expanded';
    if (state.poweredUpId === c.id) cardClasses += ' hoh-trial-card-powered hoh-trial-card-powered-' + state.poweredUpHearts;
    var bodyClass = 'hoh-trial-card-body' + (state.cardExpanded ? ' hoh-trial-card-body-expanded' : '');
    var readHint = state.cardExpanded ? 'Close' : 'Read full story';
    var storyBlock = state.cardExpanded
      ? '<div class="hoh-trial-card-story-full">' + (fullStory || '<em>No story written yet.</em>') + '<div class="hoh-trial-card-scroll-hint" id="hoh-trial-card-scroll-hint"><span>Scroll to read</span><span class="hoh-trial-card-scroll-chev">&#8595;</span></div></div>'
      : '<div class="hoh-trial-card-story-clamp">' + (fullStory || '<em>No story written yet.</em>') + '</div>';
    /* Image-bottom hint. Three states:
       default:  tap to enlarge the image
       expanded: tap to shrink back
       powered:  tap for a bonus heart (color matches next heart tier) */
    var hintText, hintClass;
    if (state.poweredUpId === c.id){
      hintClass = 'hoh-trial-card-img-hint hoh-trial-card-img-hint-power';
      hintText = state.poweredUpHearts >= TRIAL_PER_CHAR_CAP
        ? 'Added to your likes &#9829;&#xFE0E;'
        : 'Tap for a bonus heart &#9829;';
    } else if (state.cardImageExpanded){
      hintClass = 'hoh-trial-card-img-hint';
      hintText = 'Tap to shrink';
    } else {
      hintClass = 'hoh-trial-card-img-hint';
      hintText = 'Tap to enlarge';
    }
    /* Live community-heart count badge top-right of the image. Updates
       as the user awards hearts via DOM mutation (see applyHeartLanded). */
    var heartCount = c.hearts || 0;
    var heartBadge = '<div class="hoh-trial-card-hearts-badge"><span class="hoh-trial-card-hearts-badge-icon">&#9829;</span><span class="hoh-trial-card-hearts-badge-count">' + heartCount + '</span></div>';
    return '<div class="' + cardClasses + '" data-id="' + esc(c.id) + '" data-tier="' + rar.tier + '">' +
      '<div class="hoh-trial-card-media' + (hasMulti ? ' hoh-trial-card-media-multi' : '') + '" onclick="event.stopPropagation();hohTrialOnImageTap()">' +
        media +
        '<div class="hoh-trial-card-media-fade"></div>' +
        heartBadge +
        arrowsHtml +
        dotsHtml +
        '<div class="' + hintClass + '">' + hintText + '</div>' +
      '</div>' +
      '<div class="' + bodyClass + '" onclick="hohTrialToggleExpand()">' +
        '<h3 class="hoh-trial-card-name">' + esc(c.name || 'Unnamed') + '</h3>' +
        '<p class="hoh-trial-card-meta">' + esc(c.race || '') + ' ' + esc(c.character_class || '') + lv + '</p>' +
        campaign +
        storyBlock +
        '<div class="hoh-trial-card-foot">' +
          '<span class="hoh-trial-card-byline">submitted by ' + esc(c.player_name || 'Anonymous') + '</span>' +
          '<span class="hoh-trial-card-expand-hint"><span>' + readHint + '</span><span class="hoh-trial-card-expand-chev">&rsaquo;</span></span>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function renderDeck(){
    var host = document.getElementById('hoh-trial-deck');
    if (!host) return;
    state.cardExpanded = false;
    state.cardImageExpanded = false;
    state.cardImgIdx = 0;
    state.poweredUpId = null;
    state.poweredUpHearts = 0;
    if (state.poweredUpTimer){ clearTimeout(state.poweredUpTimer); state.poweredUpTimer = null; }
    state.advancing = false;
    var topChar = resolveChar(state.deck[0]);
    host.innerHTML = topChar ? topCardHtml(topChar) : '';
  }

  function rerenderTopCardInPlace(){
    var host = document.getElementById('hoh-trial-deck');
    if (!host) return;
    var topChar = resolveChar(state.deck[0]);
    host.innerHTML = topChar ? topCardHtml(topChar) : '';
  }

  /* ============ heart pop ============ */
  /* The heart pops on the card image it was given to. Jan, 2026-09-27:
     hearts in the Hall no longer fly from place to place; they burst
     where they land (hohHeartBurst in hoh-wallet.js). fromEl and heartNum
     stay for the caller; the card's own pulse still marks the tier. */
  function flyHeartIntoCard(fromEl, toEl, heartNum){
    var at = toEl || fromEl;
    if (!at || typeof window.hohHeartBurst !== 'function') return;
    window.hohHeartBurst(at, { n: 1 });
  }

  /* Brief pop on the card itself when a heart lands. Adds a class then
     removes it so the next pop can fire again. */
  function pulseCardOnLand(card, color){
    if (!card) return;
    card.classList.remove('hoh-trial-card-pulse-gold','hoh-trial-card-pulse-blue','hoh-trial-card-pulse-purple');
    void card.offsetWidth;
    card.classList.add('hoh-trial-card-pulse-' + color);
    setTimeout(function(){ if (card) card.classList.remove('hoh-trial-card-pulse-' + color); }, 700);
  }

  /* ============ image taps + story expand ============ */
  window.hohTrialOnImageTap = function(){
    var topId = state.deck[0];
    if (!topId) return;
    /* In powered-up state, image taps award bonus hearts. */
    if (state.poweredUpId === topId){
      tryAwardBonusHeart();
      return;
    }
    /* Default state: image tap zooms the image bigger. Tapping again
       shrinks it back. Body text taps still toggle the story expand. */
    if (state.cardExpanded) state.cardExpanded = false;
    state.cardImageExpanded = !state.cardImageExpanded;
    rerenderTopCardInPlace();
  };

  window.hohTrialCycleImage = function(dir){
    var topId = state.deck[0];
    if (!topId) return;
    var c = resolveChar(topId);
    if (!c) return;
    var imgs = getImgsForCard(c);
    if (imgs.length < 2) return;
    var step = (dir === -1) ? -1 : 1;
    state.cardImgIdx = (state.cardImgIdx + step + imgs.length) % imgs.length;
    rerenderTopCardInPlace();
  };

  window.hohTrialToggleExpand = function(){
    if (state.cardImageExpanded) state.cardImageExpanded = false;
    state.cardExpanded = !state.cardExpanded;
    rerenderTopCardInPlace();
    if (state.cardExpanded) primeScrollHint();
  };

  function primeScrollHint(){
    setTimeout(function(){
      var hint = document.getElementById('hoh-trial-card-scroll-hint');
      var body = document.querySelector('.hoh-trial-card-story-full');
      if (!hint || !body) return;
      hint.classList.add('hoh-trial-card-scroll-hint-show');
      function fadeOut(){
        hint.classList.remove('hoh-trial-card-scroll-hint-show');
        hint.classList.add('hoh-trial-card-scroll-hint-out');
        body.removeEventListener('scroll', fadeOut);
      }
      body.addEventListener('scroll', fadeOut, { passive: true, once: true });
      setTimeout(fadeOut, 2800);
    }, 60);
  }

  /* ============ main flow: skip / heart / power-up bonus ============ */
  /* Skip: pass on the card with no heart given. Advances immediately. */
  window.hohTrialSkip = function(){
    if (state.advancing) return;
    var topId = state.deck[0];
    if (!topId) return;
    var card = document.querySelector('.hoh-trial-card-top');
    if (!card) return;
    /* Cancel any pending power-up timer cleanly. */
    if (state.poweredUpTimer){ clearTimeout(state.poweredUpTimer); state.poweredUpTimer = null; }
    state.advancing = true;
    animateOff(card, 'left', function(){ advanceDeck(topId); });
  };

  /* Like button. Behavior depends on state:
     - Default: gives the FIRST heart, enters powered-up state, holds.
     - Powered up (1 or 2 hearts so far): gives a bonus heart; advances
       early if this is the 3rd heart. */
  window.hohTrialLike = function(){
    if (state.advancing) return;
    var topId = state.deck[0];
    if (!topId) return;
    if (getHearts() <= 0){
      endSession();
      return;
    }
    if (state.poweredUpId === topId){
      tryAwardBonusHeart();
      return;
    }
    /* First heart on this card. */
    awardHeart(true /*isFirst*/);
  };

  /* Fires when a heart given inside Tinder crosses a 10-heart threshold
     and earns the user a raffle ticket. Spawns a short rainbow-ticket
     burst over the top card so the player feels the milestone, plus a
     larger banner that slides in from the top of the modal. */
  function fireTicketBurst(){
    var stage = document.getElementById('hoh-trial-swiper') || document.body;
    var card = document.querySelector('.hoh-trial-card-top');
    var anchor = card || stage;
    var rect = anchor.getBoundingClientRect();
    /* Top-of-card burst: ticket icon + "+1 ticket" label, scales up and
       drifts upward, fades out over ~1.4s. */
    var burst = document.createElement('div');
    burst.className = 'hoh-trial-ticket-burst';
    burst.style.cssText =
      'position:fixed;left:' + (rect.left + rect.width/2) + 'px;' +
      'top:' + (rect.top + rect.height * 0.18) + 'px;' +
      'transform:translate(-50%,-50%);pointer-events:none;z-index:99999;';
    burst.innerHTML =
      '<span class="hoh-tk-icon" style="width:48px;height:48px;display:block;margin:0 auto;"></span>' +
      '<div class="hoh-trial-ticket-burst-label">+1 raffle ticket</div>';
    document.body.appendChild(burst);
    setTimeout(function(){ if (burst.parentNode) burst.parentNode.removeChild(burst); }, 1500);
    /* Card glow: brief rainbow ring around the card to reinforce the
       ticket coming from this hero's heart. */
    if (card){
      card.classList.remove('hoh-trial-card-ticket-pop');
      void card.offsetWidth;
      card.classList.add('hoh-trial-card-ticket-pop');
      setTimeout(function(){ if (card) card.classList.remove('hoh-trial-card-ticket-pop'); }, 900);
    }
  }

  function awardHeart(isFirst){
    var topId = state.deck[0];
    if (!topId) return;
    var c = resolveChar(topId);
    if (!c) return;
    var card = document.querySelector('.hoh-trial-card-top');
    var img = card && card.querySelector('.hoh-trial-card-img, .hoh-trial-card-letter');
    var fromBtn = document.querySelector('.hoh-trial-like');
    /* Capture rarity BEFORE mutating c.hearts so we can detect a tier
       upgrade after the bump. */
    var rarFn = window.hohGetRarity || function(){ return { tier:'common', label:'Common' }; };
    var oldHearts = c.hearts || 0;
    var oldRar = rarFn(oldHearts);
    /* Decrement pool, record liked, mirror to lifetime. */
    setHearts(getHearts() - 1);
    addLiked(c);
    mirrorHearted(topId);
    /* Auto-add to Your Hall on a Tinder like. Same auto-save rule as
       the detail modal: respects the explicit removed list so a
       previously-removed hero is NOT re-added by a Tinder swipe. */
    try { if (typeof window.hohHallSaveHero === 'function') window.hohHallSaveHero(topId, 'tinder'); } catch(_){}
    /* Per-heart ticket grants are retired. We still call the heart
       RPC to record the heart server-side (and trigger any cap or
       slowdown logic), but we ignore any `ticket` payload it echoes —
       tickets only come from daily quests now. */
    try {
      if (window.hohSupabaseClient && typeof window.hohSupabaseClient.rpc === 'function'){
        var em = (typeof window.hohYourHallEmail === 'function') ? window.hohYourHallEmail() : '';
        var aFn = window.hohGetHearterAttribution;
        var attr = (typeof aFn === 'function') ? aFn() : { sessionId: null, name: '', public: false };
        window.hohSupabaseClient.rpc('increment_hearts_capped', {
          row_id: topId,
          hearter_session_id: attr.sessionId,
          hearter_name: (attr.public ? attr.name : null),
          is_public: !!attr.public,
          hearter_email: em || null
        }).then(function(){
          /* Intentionally no-op on the ticket payload — heart-driven
             ticket grants are retired. */
        }).catch(function(){});
      }
    } catch(e){}
    c.hearts = oldHearts + 1;
    var newRar = rarFn(c.hearts);
    animateHeartSpend();
    var heartNum = state.poweredUpHearts + 1;
    var color = heartNum === 1 ? 'gold' : (heartNum === 2 ? 'blue' : 'purple');
    state.poweredUpId = topId;
    state.poweredUpHearts = heartNum;
    /* Mutate the existing card DOM in place rather than re-rendering, so
       the in-flight heart element is undisturbed and the user does not
       see a card flash mid-flight. The class swaps and badge update are
       all the visible changes needed. */
    if (card){
      card.classList.add('hoh-trial-card-powered','hoh-trial-card-powered-' + heartNum);
      card.classList.remove('hoh-trial-card-powered-1','hoh-trial-card-powered-2','hoh-trial-card-powered-3');
      card.classList.add('hoh-trial-card-powered-' + heartNum);
      /* Live community-heart count update. */
      var countEl = card.querySelector('.hoh-trial-card-hearts-badge-count');
      if (countEl) countEl.textContent = c.hearts;
      var badgeEl = card.querySelector('.hoh-trial-card-hearts-badge');
      if (badgeEl){
        badgeEl.classList.remove('hoh-trial-card-hearts-badge-bump');
        void badgeEl.offsetWidth;
        badgeEl.classList.add('hoh-trial-card-hearts-badge-bump');
      }
      /* Hint copy + tier color cue. With cap=1 the user always sees the
         "added" message after their single heart; the multi-heart
         "Tap for a bonus heart" path is dead but kept for safety. */
      var hint = card.querySelector('.hoh-trial-card-img-hint');
      if (hint){
        hint.classList.add('hoh-trial-card-img-hint-power');
        hint.innerHTML = (heartNum >= TRIAL_PER_CHAR_CAP)
          ? 'Added to your likes &#9829;&#xFE0E;'
          : 'Tap for a bonus heart &#9829;';
      }
      /* Rarity tier upgrade. Swap the tier class on the card and fire
         the host's celebration if it crossed a threshold. */
      if (oldRar.tier !== newRar.tier){
        card.classList.remove('hoh-trial-rar-' + oldRar.tier);
        card.classList.add('hoh-trial-rar-' + newRar.tier);
        try {
          if (typeof window.hohCelebrateTierUp === 'function'){
            window.hohCelebrateTierUp(card, oldRar.tier, newRar.tier, newRar);
          }
        } catch(e){}
      }
    }
    flyHeartIntoCard(fromBtn || img, img, heartNum);
    setTimeout(function(){
      var liveCard = document.querySelector('.hoh-trial-card-top');
      pulseCardOnLand(liveCard || card, color);
    }, 380);
    /* If maxed or out of pool, fire a quick exit. */
    if (state.poweredUpHearts >= TRIAL_PER_CHAR_CAP || getHearts() <= 0){
      schedulePoweredUpAdvance(POWERUP_HOLD_MAX_MS);
      return;
    }
    /* First heart: short initial hold. Bonus heart: extend the existing
       hold by a small amount instead of resetting it. */
    if (isFirst){
      schedulePoweredUpAdvance(POWERUP_HOLD_INITIAL_MS);
    } else {
      schedulePoweredUpAdvance(POWERUP_HOLD_EXTEND_MS);
    }
  }

  function tryAwardBonusHeart(){
    var topId = state.deck[0];
    if (!topId) return;
    if (state.poweredUpHearts >= TRIAL_PER_CHAR_CAP) return;
    if (getHearts() <= 0){ schedulePoweredUpAdvance(0); return; }
    awardHeart(false);
  }

  function schedulePoweredUpAdvance(ms){
    if (state.poweredUpTimer){ clearTimeout(state.poweredUpTimer); state.poweredUpTimer = null; }
    state.poweredUpTimer = setTimeout(function(){
      var card = document.querySelector('.hoh-trial-card-top');
      if (!card){ advanceDeck(state.poweredUpId || state.deck[0]); return; }
      var leavingId = state.deck[0];
      state.advancing = true;
      animateOff(card, 'right', function(){ advanceDeck(leavingId); });
    }, ms);
  }

  function animateOff(card, direction, after){
    var x = direction === 'right' ? (window.innerWidth + 200) : -(window.innerWidth + 200);
    var rot = direction === 'right' ? 14 : -14;
    card.classList.add('hoh-trial-card-leaving');
    card.style.transition = 'transform 0.34s cubic-bezier(0.3,0.7,0.4,1), opacity 0.34s ease';
    card.style.transform = 'translate(' + x + 'px,0) rotate(' + rot + 'deg)';
    card.style.opacity = '0';
    setTimeout(function(){ if (typeof after === 'function') after(); }, 340);
  }

  function advanceDeck(leavingId){
    if (leavingId) addSeen(leavingId);
    state.deck.shift();
    saveDeck(state.deck);
    if (getHearts() <= 0){ endSession(); return; }
    if (!state.deck.length){ endSession(); return; }
    renderDeck();
  }

  /* ============ summary ============ */
  function endSession(){
    var summary = document.getElementById('hoh-trial-summary');
    if (!summary) return;
    showScreen('summary');
    /* Hero's Circle completion reward: +10 hearts back to the
       user's bank. No raffle tickets — those come from Wander the
       Hall only. The 10 hearts they spent inside the Circle are
       returned, making it a "free" activity that costs nothing
       net but delivers the discovery experience. */
    var grantedNow = false;
    try {
      if(typeof window.hohAddHeartsToBank === 'function'){
        window.hohAddHeartsToBank(TRIAL_COMPLETION_BONUS_HEARTS, 'earn_bonus');
        grantedNow = true;
      }
    } catch(e){}
    /* Legacy bonus call retained for any older build still wiring
       that path; safe no-op if it's not defined. */
    try { if(window.hohGrantTrialCompBonus) window.hohGrantTrialCompBonus(); } catch(e){}
    /* Grant 2 daily raffle tickets via the server RPC. Auto-flows to
       the user's most recent character. Marks roll_call_completed so
       Your Hall unlocks. Local flag also set so the unlock state
       reflects immediately even before the network round-trip. */
    try {
      localStorage.setItem('hoh-daily-tinder-' + (new Date().toISOString().slice(0,10)), '1');
    } catch(e){}
    try {
      var sb = window.hohSupabaseClient;
      var em = (typeof window.hohYourHallEmail === 'function') ? window.hohYourHallEmail() : '';
      var raw = '';
      try { raw = localStorage.getItem('hoh-your-hall-token') || ''; } catch(_){}
      var pin = '';
      if (raw) { try { var t = JSON.parse(raw); pin = t && t.pin || ''; } catch(_){} }
      if (sb && em && pin) {
        /* Hero's Circle no longer carries the daily-quest 2-ticket
           completion bonus. The daily quest is now "Explore the Hall"
           — heart 20 heroes at the Fresh Faces section. The tinder
           hearts that the user gives still count toward the per-10
           server-side ticket cap (via increment_hearts_capped during
           the session), so users who happen to use the Circle still
           bank tickets at the same rate; they just don't get a
           separate completion bonus on top.

           If you ever want to re-enable the bonus, restore the rpc
           call below with target_total: 2. */
        if (typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
        if (typeof window.hohFetchRaffleState === 'function') window.hohFetchRaffleState();
      } else {
        /* Not signed in: still set local flag so the toggle reflects
           the day's completion in this browser session. */
        if (typeof window.hohRefreshYhToggle === 'function') window.hohRefreshYhToggle();
      }
    } catch(e){ console.warn('[Tinder] complete', e); }
    var liked = getLiked();
    var arr = Object.keys(liked).map(function(k){ return liked[k]; });
    var bonusArr = arr.filter(function(x){ return (x.hearts || 0) >= 2; });
    var totalHearts = arr.reduce(function(sum, x){ return sum + (x.hearts || 0); }, 0);
    var headline = arr.length
      ? 'You gave ' + totalHearts + ' heart' + (totalHearts === 1 ? '' : 's') + ' tonight.'
      : 'No hearts spent tonight.';
    /* Bonus character chips: each character who got 2 or 3 hearts. */
    var bonusHtml = '';
    if (bonusArr.length){
      var chips = bonusArr.map(function(x){
        var initial = esc((x.name || '?').charAt(0));
        var media = x.image_url
          ? '<img src="' + esc(x.image_url) + '" alt="" crossorigin="anonymous" loading="lazy" data-letter="' + initial + '" onerror="if(window.hohImgFallback)hohImgFallback(this,\'hoh-trial-bonus-letter\');else this.style.display=\'none\';">'
          : '<div class="hoh-trial-bonus-letter">' + initial + '</div>';
        var nh = x.hearts || 0;
        var hearts = '';
        for (var k = 0; k < nh; k++){
          var col = k === 0 ? 'gold' : (k === 1 ? 'blue' : 'purple');
          hearts += '<span class="hoh-trial-bonus-heart hoh-trial-bonus-heart-' + col + '">&#9829;</span>';
        }
        return '<a class="hoh-trial-bonus-chip" href="/pages/hall-of-heroes?hero=' + esc(x.slug || x.id) + '">' +
          '<div class="hoh-trial-bonus-thumb">' + media + '</div>' +
          '<div class="hoh-trial-bonus-info">' +
            '<div class="hoh-trial-bonus-name">' + esc(x.name) + '</div>' +
            '<div class="hoh-trial-bonus-hearts">' + hearts + '</div>' +
          '</div>' +
        '</a>';
      });
      bonusHtml =
        '<div class="hoh-trial-bonus-block">' +
          '<div class="hoh-trial-bonus-label">Bonus hearts given</div>' +
          '<div class="hoh-trial-bonus-list">' + chips.join('') + '</div>' +
        '</div>';
    }
    /* Marquee of every liked character (single + bonus). */
    var thumbCards = arr.map(function(x){
      var initial = esc((x.name || '?').charAt(0));
      var media = x.image_url
        ? '<img src="' + esc(x.image_url) + '" alt="' + esc(x.name) + '" crossorigin="anonymous" loading="lazy" data-letter="' + initial + '" onerror="if(window.hohImgFallback)hohImgFallback(this,\'hoh-trial-marquee-letter\');else this.style.display=\'none\';">'
        : '<div class="hoh-trial-marquee-letter">' + initial + '</div>';
      return '<a class="hoh-trial-marquee-card" href="/pages/hall-of-heroes?hero=' + esc(x.slug || x.id) + '">' + media + '<div class="hoh-trial-marquee-name">' + esc(x.name) + '</div></a>';
    });
    var trackCards = '';
    var copies = arr.length <= 4 ? 4 : (arr.length <= 8 ? 2 : 1);
    for (var c = 0; c < copies; c++){ trackCards += thumbCards.join(''); }
    trackCards += thumbCards.join('');
    var marqueeHtml = arr.length
      ? '<div class="hoh-trial-marquee-wrap"><div class="hoh-trial-marquee-track">' + trackCards + '</div></div>'
      : '';
    /* Reveal banner: +10 hearts back to the user's bank. The Circle
       used to grant 2 raffle tickets — those moved to the daily
       Wander quest at Fresh Faces. The Circle is now a free
       discovery activity; the user spent 10 hearts and gets 10
       back, so it costs nothing net but delivers the experience. */
    /* R62: stripped the trailing two heart icons — copy already says
       "+10 hearts" so the extra glyphs were redundant noise. */
    var rewardHtml =
      '<div class="hoh-trial-reward" id="hoh-trial-reward">' +
        '<span class="hoh-trial-reward-text"><strong>Round complete.</strong> Thanks for playing.</span>' +
      '</div>';
    summary.innerHTML =
      '<div class="hoh-trial-summary-inner">' +
        '<div class="hoh-trial-summary-eyebrow">The circle closes</div>' +
        '<h3 class="hoh-trial-summary-title">' + headline + '</h3>' +
        rewardHtml +
        bonusHtml +
        marqueeHtml +
        '<p class="hoh-trial-summary-note">A new door opens at midnight.</p>' +
        '<button class="hoh-trial-summary-btn" onclick="hohCloseTrial()">Return to the Hall</button>' +
      '</div>';
    try {
      if (typeof confetti === 'function'){
        confetti({ particleCount: 80, spread: 70, startVelocity: 35, gravity: 0.4, origin: { x: 0.5, y: 0.32 }, colors: ['#c8a45e','#ee99fe','#a4d8ff','#ffd966'], zIndex: 999999 });
      }
    } catch(e){}
  }

  /* ============ open / close ============ */
  window.hohOpenTrial = function(){
    var coinModal = document.getElementById('hoh-coin-modal');
    if (coinModal && coinModal.style.display !== 'none') coinModal.style.display = 'none';
    var modal = document.getElementById('hoh-trial-modal');
    if (!modal) return;
    if (sget(heartsKey(), null) == null) sset(heartsKey(), String(TRIAL_DAILY_HEARTS));
    state.deck = buildOrLoadDeck();
    var seen = getSeen();
    state.totalToday = state.deck.length + seen.length;
    var sumEl = document.getElementById('hoh-trial-summary');
    if (sumEl){ sumEl.innerHTML = ''; }
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    if (!introSeenToday()){
      showScreen('intro');
    } else {
      showScreen('swiper');
      renderHearts(true);
      if (getHearts() <= 0 || !state.deck.length){ endSession(); return; }
      renderDeck();
    }
  };
  window.hohTrialBegin = function(){
    markIntroSeen();
    showScreen('swiper');
    if (!state.deck.length){
      state.deck = buildOrLoadDeck();
      var seen = getSeen();
      state.totalToday = state.deck.length + seen.length;
    }
    renderHearts(true);
    if (getHearts() <= 0 || !state.deck.length){ endSession(); return; }
    renderDeck();
  };
  window.hohCloseTrial = function(){
    var modal = document.getElementById('hoh-trial-modal');
    if (modal) modal.style.display = 'none';
    document.body.style.overflow = '';
    state.cardExpanded = false;
    if (state.poweredUpTimer){ clearTimeout(state.poweredUpTimer); state.poweredUpTimer = null; }
    if (typeof window.forceCardsVisible === 'function'){ try { window.forceCardsVisible(); } catch(e){} }
    if (typeof window.hohApplyFilters === 'function'){ try { window.hohApplyFilters(); } catch(e){} }
  };

  document.addEventListener('keydown', function(e){
    var modal = document.getElementById('hoh-trial-modal');
    if (!modal || modal.style.display === 'none') return;
    if (e.key === 'Escape'){ window.hohCloseTrial(); return; }
    var swiperVisible = document.getElementById('hoh-trial-swiper') && document.getElementById('hoh-trial-swiper').style.display !== 'none';
    if (!swiperVisible) return;
    if (e.key === 'ArrowRight'){ window.hohTrialLike(); }
    else if (e.key === 'ArrowLeft'){ window.hohTrialSkip(); }
  });
})();
